import React from 'react';
import { View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { OnCardContext } from '../fieldSurface';
import PressableScale from './PressableScale';

/**
 * Card: rounded container for grouped content.
 * Props:
 * - tone: 'default' (card) | 'raised' | 'current' (outlined, the thing to do now) | 'success' | 'warning'
 * - onPress: makes the whole card a button (pass accessibilityLabel too)
 * - padded (default true): 16 px padding; false for edge-to-edge content
 * - style, children, accessibilityLabel, accessibilityHint
 * Form fields inside a Card use the canvas fill (design/fieldSurface.js).
 */
const Card = ({
	tone = 'default',
	onPress,
	padded = true,
	style,
	children,
	accessibilityLabel,
	accessibilityHint,
}) => {
	const styles = useThemedStyles(createStyles);
	const cardStyle = [styles.card, styles[tone], padded && styles.padded, style];

	const content = <OnCardContext.Provider value={true}>{children}</OnCardContext.Provider>;

	if (!onPress) {
		return <View style={cardStyle}>{content}</View>;
	}

	return (
		<PressableScale
			pressedScale={0.98}
			onPress={onPress}
			accessibilityRole="button"
			accessibilityLabel={accessibilityLabel}
			accessibilityHint={accessibilityHint}
			style={({ pressed }) => [cardStyle, pressed && styles.pressed]}
		>
			{content}
		</PressableScale>
	);
};

const createStyles = ({ ui: { colors, radius, space } }) => ({
	card: {
		borderRadius: radius.hero,
		borderWidth: 1,
		borderColor: colors.card,
		backgroundColor: colors.card,
	},
	padded: {
		padding: space.lg,
	},
	default: {},
	raised: {
		backgroundColor: colors.raised,
		borderColor: colors.raisedBorder,
	},
	current: {
		backgroundColor: colors.current,
		borderColor: colors.currentBorder,
		borderWidth: 2,
	},
	success: {
		backgroundColor: colors.successCard,
		borderColor: colors.successCardBorder,
	},
	warning: {
		backgroundColor: colors.warningBg,
		borderColor: colors.warningBorder,
	},
	pressed: {
		opacity: 0.85,
	},
});

export default Card;
